import { useMemo, useState, useRef, useCallback, useEffect, ChangeEvent, useContext } from 'react'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import clsx from 'clsx'

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { useGetIdsUsersCurrentHealthcareservices } from '@toktokdoctech/tokapi-client'
import { useButtonsStyles } from '../../../styles'
import { HealthCareServicesListProps, ScrollButtonState } from './_props'
import sxs from './_sxs'
import NoHcsLinked from '../../Common/noHcsLinked/NoHcsLinked'
import { CurrentHcsContext } from '../hcs/CurrentHcsProvider/CurrentHcsContext'
import { currentHcsStorageKey, useSelectHcs } from '../hcs/CurrentHcsProvider/useSelectHcs'

const scrollButtonValue: ScrollButtonState = {
    maxScroll: undefined,
    display: false,
}

const HealthCareServicesList = ({ onCancel, onSelect, closeButtonText }: HealthCareServicesListProps) => {
    const { currentHcs } = useContext(CurrentHcsContext)
    const selectHcs = useSelectHcs()
    const {
        data: userHealthCareServices,
        isLoading,
        error,
    } = useGetIdsUsersCurrentHealthcareservices({ query: { staleTime: 5000 } })
    const [storedHcs] = useState<string | null>(localStorage.getItem(currentHcsStorageKey) ?? null)
    const buttonClasses = useButtonsStyles()
    const [searchText, setSearchText] = useState('')
    const [scrollButton, setScrollButton] = useState<ScrollButtonState>(scrollButtonValue)
    const hscListRef = useRef<HTMLUListElement | null>(null)
    const isScrollable = scrollButton.maxScroll !== undefined && scrollButton.maxScroll > 0

    const handleMaxScroll = useCallback(() => {
        if (hscListRef?.current && !isLoading) {
            const { scrollHeight, clientHeight, scrollTop } = hscListRef.current

            const res = scrollButton.maxScroll !== undefined && scrollTop < scrollButton.maxScroll - 20

            setScrollButton(() => ({
                maxScroll: scrollHeight - clientHeight,
                display: res,
            }))
        } else {
            setScrollButton((previousButtonState) => ({
                ...previousButtonState,
                maxScroll: undefined,
            }))
        }
    }, [hscListRef, isLoading, scrollButton.maxScroll])

    useEffect(() => {
        handleMaxScroll()
        window.addEventListener('resize', handleMaxScroll, true)
        return () => {
            window.removeEventListener('resize', handleMaxScroll, true)
        }
    }, [handleMaxScroll])

    useEffect(() => {
        handleMaxScroll()
    }, [handleMaxScroll, searchText])

    const handleHcsSelection = useCallback(
        async (healthCareServiceId: string) => {
            if (healthCareServiceId !== currentHcs?.id) {
                await selectHcs(healthCareServiceId)
                onSelect?.()
            }
        },
        [currentHcs?.id, onSelect, selectHcs],
    )

    const handleScroll = () => {
        hscListRef.current?.scrollBy({
            top: 300,
            left: 0,
            behavior: 'smooth',
        })
    }

    const filteredHcs = useMemo(
        () =>
            userHealthCareServices?.filter((hcs) => hcs.name?.toLowerCase().includes(searchText.toLocaleLowerCase())) ??
            [],
        [userHealthCareServices, searchText],
    )

    if (error) {
        throw new Error('Error loading healthCareServices.')
    }

    useEffect(() => {
        if (userHealthCareServices) {
            if (!currentHcs && !!storedHcs && userHealthCareServices.some((hcs) => hcs.id === storedHcs)) {
                handleHcsSelection(storedHcs)
            }
            if (userHealthCareServices.length === 1) {
                const hcs = userHealthCareServices.pop()
                if (!hcs) throw new Error('[HealthCareServiceList] - hcs should have length one but was empty!') // for ts inference
                handleHcsSelection(hcs.id)
            }
        }
    }, [currentHcs, handleHcsSelection, storedHcs, userHealthCareServices])

    if (userHealthCareServices?.length === 0) {
        return <NoHcsLinked />
    }

    const handleSearch = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearchText(event.target.value)
    }

    return (
        <>
            <TextField
                variant="standard"
                value={searchText}
                placeholder="Recherche par nom d'établissement"
                onChange={(event) => handleSearch(event)}
                sx={sxs.HealthCareServicesListTextField}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    endAdornment: searchText && (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setSearchText('')} size="large">
                                <ClearIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <Box sx={sxs.HealthCareServicesListContainer}>
                <List
                    ref={hscListRef}
                    onScroll={handleMaxScroll}
                    sx={[sxs.HealthCareServicesList, isScrollable && sxs.HealthCareServicesListScrollable]}
                >
                    <ListItem>
                        <Box sx={sxs.HealthCareServicesListItemInnerContainer}>
                            <InfoRoundedIcon sx={sxs.HealthCareServicesListItemInnerContainerIcon} />
                            <Typography sx={sxs.HealthCareServicesListItemInnerContainerText}>
                                Merci de sélectionner le centre de santé sur lequel vous désirez vous connecter.
                            </Typography>
                        </Box>
                    </ListItem>
                    {filteredHcs && filteredHcs.length === 0 && (
                        <ListItem>
                            <ListItemText primary="Aucun résultat." />
                        </ListItem>
                    )}
                    {!isLoading ? (
                        <div data-cy="dataCy-healthCareService-list">
                            {filteredHcs.map((healthCareService) => (
                                <ListItem
                                    button
                                    key={healthCareService.id}
                                    onClick={() => handleHcsSelection(healthCareService.id)}
                                    data-cy={`dataCy-hcsListItem-${healthCareService.id}`}
                                >
                                    <ListItemText
                                        sx={
                                            healthCareService.id === currentHcs?.id
                                                ? sxs.HealthCareServicesListItemActive
                                                : null
                                        }
                                        primary={healthCareService.name}
                                    />
                                </ListItem>
                            ))}
                        </div>
                    ) : (
                        <CircularProgress />
                    )}
                </List>
                {scrollButton.display && (
                    <Box sx={sxs.HealthCareServicesListScrollButtonContainer}>
                        <Tooltip title="Faire défiler la liste">
                            <IconButton
                                aria-label="descendre"
                                onClick={handleScroll}
                                sx={sxs.HealthCareServicesListScrollButton}
                            >
                                <ExpandMoreRoundedIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
            <Box sx={sxs.HealthCareServicesListButtonContainer}>
                <Button
                    variant="contained"
                    disableElevation
                    className={clsx(buttonClasses.ButtonBlack, buttonClasses.ButtonLarge)}
                    onClick={onCancel}
                >
                    {closeButtonText}
                </Button>
            </Box>
        </>
    )
}

export default HealthCareServicesList
